import { Fragment, useMemo } from "react";
import {
  AlertCircle,
  Loader2,
  RefreshCw,
  Search,
  Users,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "@/components/ui/pagination";
import { MappingStatsBar } from "@/components/MappingStatsBar";
import { SubdomainGroup } from "@/components/SubdomainGroup";
import { FileMappingGroup } from "@/components/FileMappingGroup";
import { FlatTableFallback } from "@/components/FlatTableFallback";
import { UserPicker } from "@/components/UserPicker";
import type { TesterMapping } from "@/hooks/useWizardPersistence";
import type { TesterUser } from "@/services/testers";
import type { SelectedExcelFile, ParseExcelResponse } from "@/types/api";

const FLAT_PAGE_SIZE = 20;
const GROUP_PAGE_SIZE = 10;

interface TesterMappingStepProps {
  testerMappings: TesterMapping[];
  mappingSearch: string;
  expandedSubdomains: Set<string>;
  expandedMappingFiles: Set<string>;
  scenarioStepKeys: Set<string>;
  selectedTestFiles: SelectedExcelFile[];
  parseResult: ParseExcelResponse | null;

  isTestersLoading: boolean;
  isTestersError: boolean;
  isResyncingMappings: boolean;
  parseExcelIsPending: boolean;
  isPolling: boolean;
  cognitoUsers: TesterUser[];

  onMappingSearchChange: (value: string) => void;
  onSubdomainToggle: (key: string, open: boolean) => void;
  onFileToggle: (file: string, open: boolean) => void;
  onResolve: (mappingId: string, personIndex: number, user: TesterUser) => void;
  onRemove: (mappingId: string, personIndex: number) => void;
  onAddUser: (mappingId: string, user: TesterUser) => void;
  onAssignToAll: (user: TesterUser) => void;
  onResync: () => void;
  onRetryTesters: () => void;

  mappingPage: number;
  onMappingPageChange: (page: number) => void;
}

export function TesterMappingStep({
  testerMappings,
  mappingSearch,
  expandedSubdomains,
  expandedMappingFiles,
  scenarioStepKeys,
  selectedTestFiles,
  parseResult,
  isTestersLoading,
  isTestersError,
  isResyncingMappings,
  parseExcelIsPending,
  isPolling,
  cognitoUsers,
  onMappingSearchChange,
  onSubdomainToggle,
  onFileToggle,
  onResolve,
  onRemove,
  onAddUser,
  onAssignToAll,
  onResync,
  onRetryTesters,
  mappingPage,
  onMappingPageChange,
}: TesterMappingStepProps) {
  const sortedMappings = useMemo(() =>
    [...testerMappings].sort((a, b) => {
      const domainCompare = a.domain.toLowerCase().localeCompare(b.domain.toLowerCase());
      if (domainCompare !== 0) return domainCompare;
      const testerCompare = a.tester.toLowerCase().localeCompare(b.tester.toLowerCase());
      if (testerCompare !== 0) return testerCompare;
      return a.area.localeCompare(b.area);
    }),
    [testerMappings]
  );

  const filteredMappings = useMemo(() => {
    if (!mappingSearch.trim()) return sortedMappings;
    const q = mappingSearch.toLowerCase();
    return sortedMappings.filter(m =>
      m.domain.toLowerCase().includes(q) ||
      m.tester.toLowerCase().includes(q) ||
      m.area.toLowerCase().includes(q) ||
      (m.subdomain || "").toLowerCase().includes(q) ||
      (m.scenarioFiles ?? []).some(f => f.toLowerCase().includes(q)) ||
      m.testPersons.some(p =>
        p.excelName.toLowerCase().includes(q) ||
        (p.cognitoUserName || "").toLowerCase().includes(q) ||
        (p.cognitoEmail || "").toLowerCase().includes(q)
      )
    );
  }, [sortedMappings, mappingSearch]);

  const hasSubdomainData = sortedMappings.some(m => m.subdomain);
  const totalPersons = sortedMappings.reduce((n, m) => n + m.testPersons.length, 0);
  const resolvedCount = sortedMappings.reduce(
    (n, m) => n + m.testPersons.filter(p => p.cognitoUserId).length, 0
  );
  const fuzzyCount = sortedMappings.reduce(
    (n, m) => n + m.testPersons.filter(p => p.matchConfidence === "fuzzy").length, 0
  );
  const unresolvedCount = totalPersons - resolvedCount;

  const orderedScenarioFiles = useMemo(() => {
    const fromSelection = selectedTestFiles.map(f => f.name).filter(Boolean);
    if (fromSelection.length > 0) return fromSelection;
    return Array.from(
      new Set(
        (parseResult?.scenarios ?? []).map(s => s.name || s.file_path || "")
      )
    ).filter(Boolean);
  }, [selectedTestFiles, parseResult]);

  const buildSubdomainGroups = (mappings: TesterMapping[]) =>
    Array.from(
      mappings.reduce((groups, m) => {
        const key = m.subdomain || m.tester;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(m);
        return groups;
      }, new Map<string, TesterMapping[]>())
    );

  const renderPagination = (totalPages: number, safePage: number) => {
    if (totalPages <= 1) return null;
    return (
      <Pagination className="mt-4">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              onClick={() => onMappingPageChange(Math.max(1, safePage - 1))}
              className={cn(safePage <= 1 && "pointer-events-none opacity-50")}
            />
          </PaginationItem>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1)
            .map((p, idx, arr) => (
              <Fragment key={p}>
                {idx > 0 && arr[idx - 1] !== p - 1 && (
                  <PaginationItem><PaginationEllipsis /></PaginationItem>
                )}
                <PaginationItem>
                  <PaginationLink
                    isActive={p === safePage}
                    onClick={() => onMappingPageChange(p)}
                  >
                    {p}
                  </PaginationLink>
                </PaginationItem>
              </Fragment>
            ))}
          <PaginationItem>
            <PaginationNext
              onClick={() => onMappingPageChange(Math.min(totalPages, safePage + 1))}
              className={cn(safePage >= totalPages && "pointer-events-none opacity-50")}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    );
  };

  const renderMappingBody = () => {
    if (sortedMappings.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No testers found in the mapping file.</p>
        </div>
      );
    }

    // Multiple files: per-file collapsibles (no pagination — each file is a group)
    if (orderedScenarioFiles.length > 1) {
      return (
        <div className="space-y-2">
          {orderedScenarioFiles.map((scenarioFile) => {
            const fileMappings = filteredMappings.filter(m => {
              const sf = m.scenarioFiles ?? [];
              return sf.length === 0 || sf.includes(scenarioFile);
            });
            return (
              <FileMappingGroup
                key={scenarioFile}
                scenarioFile={scenarioFile}
                fileMappings={fileMappings}
                isExpanded={expandedMappingFiles.has(scenarioFile)}
                onToggle={onFileToggle}
                hasSubdomainData={hasSubdomainData}
                expandedSubdomains={expandedSubdomains}
                onSubdomainToggle={onSubdomainToggle}
                cognitoUsers={cognitoUsers}
                isTestersLoading={isTestersLoading}
                isTestersError={isTestersError}
                onResolve={onResolve}
                onRemove={onRemove}
                onAddUser={onAddUser}
                onRetryTesters={onRetryTesters}
                mappingSearch={mappingSearch}
                scenarioStepKeys={scenarioStepKeys}
              />
            );
          })}
        </div>
      );
    }

    // Single file: no results (search filter or all removed)
    if (filteredMappings.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          <Search className="h-8 w-8 mx-auto mb-3 opacity-50" />
          {mappingSearch.trim() ? (
            <>
              <p>No mappings match &quot;{mappingSearch}&quot;</p>
              <button
                onClick={() => { onMappingSearchChange(""); onMappingPageChange(1); }}
                className="text-sm text-primary hover:underline mt-1"
              >
                Clear search
              </button>
            </>
          ) : (
            <p>No mappings to display.</p>
          )}
        </div>
      );
    }

    // Single file with subdomain data: paginate by subdomain groups
    if (hasSubdomainData) {
      const groups = buildSubdomainGroups(filteredMappings);
      const totalPages = Math.ceil(groups.length / GROUP_PAGE_SIZE);
      const safePage = Math.max(1, Math.min(mappingPage, totalPages || 1));
      const start = (safePage - 1) * GROUP_PAGE_SIZE;
      const pageGroups = groups.slice(start, start + GROUP_PAGE_SIZE);

      return (
        <>
          <div className="space-y-1.5">
            {pageGroups.map(([subdomainKey, mappingsInGroup]) => (
              <SubdomainGroup
                key={subdomainKey}
                subdomainKey={subdomainKey}
                groupDomain={mappingsInGroup[0]?.domain}
                mappingsInGroup={mappingsInGroup}
                isExpanded={expandedSubdomains.has(subdomainKey)}
                onToggle={onSubdomainToggle}
                cognitoUsers={cognitoUsers}
                isTestersLoading={isTestersLoading}
                isTestersError={isTestersError}
                onResolve={onResolve}
                onRemove={onRemove}
                onAddUser={onAddUser}
                onRetryTesters={onRetryTesters}
              />
            ))}
          </div>
          {renderPagination(totalPages, safePage)}
        </>
      );
    }

    // Single file, no subdomain data: paginate individual mappings
    const totalPages = Math.ceil(filteredMappings.length / FLAT_PAGE_SIZE);
    const safePage = Math.max(1, Math.min(mappingPage, totalPages || 1));
    const start = (safePage - 1) * FLAT_PAGE_SIZE;
    const pageMappings = filteredMappings.slice(start, start + FLAT_PAGE_SIZE);

    return (
      <>
        <FlatTableFallback
          mappings={pageMappings}
          scenarioStepKeys={scenarioStepKeys}
          cognitoUsers={cognitoUsers}
          isTestersLoading={isTestersLoading}
          isTestersError={isTestersError}
          onResolve={onResolve}
          onRemove={onRemove}
          onAddUser={onAddUser}
          onRetryTesters={onRetryTesters}
        />
        {renderPagination(totalPages, safePage)}
      </>
    );
  };

  return (
    <>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2 font-display">
              <Users className="h-5 w-5 text-primary" />
              Tester Mappings &amp; Cognito Resolution
            </CardTitle>
            <CardDescription className="mt-1">
              Review resolved Cognito users for each tester role. Manually assign unresolved names.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={onResync}
              disabled={isResyncingMappings || isTestersLoading || parseExcelIsPending || isPolling}
              className="gap-2 shrink-0"
              title="Re-parse Excel files from SharePoint and refresh Cognito user resolution"
            >
              <RefreshCw className={cn("h-4 w-4", isResyncingMappings && "animate-spin")} />
              {isResyncingMappings ? "Resyncing..." : "Resync Mappings"}
            </Button>
            {unresolvedCount > 0 && (
              <UserPicker
                mode="assign-all"
                unresolvedCount={unresolvedCount}
                onSelect={onAssignToAll}
                cognitoUsers={cognitoUsers}
                isLoading={isTestersLoading}
                isError={isTestersError}
                onRetry={onRetryTesters}
              />
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isTestersError && (
          <div className="p-4 rounded-lg bg-destructive/5 border border-destructive/20 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-destructive">Failed to load Cognito user list</p>
              <p className="text-xs text-muted-foreground">User pickers will not work until the list is loaded.</p>
            </div>
            <Button variant="outline" size="sm" onClick={onRetryTesters} className="gap-1 shrink-0">
              <RefreshCw className="h-3.5 w-3.5" />
              Retry
            </Button>
          </div>
        )}

        {isTestersLoading && (
          <div className="p-3 rounded-lg bg-muted/50 border border-border flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading Cognito users...
          </div>
        )}

        {sortedMappings.length > 0 && (
          <MappingStatsBar
            totalPersons={totalPersons}
            resolvedCount={resolvedCount}
            fuzzyCount={fuzzyCount}
            unresolvedCount={unresolvedCount}
          />
        )}

        {sortedMappings.length > 0 && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by subdomain, person, Cognito user..."
              value={mappingSearch}
              onChange={(e) => { onMappingSearchChange(e.target.value); onMappingPageChange(1); }}
              className="pl-9 pr-9"
            />
            {mappingSearch && (
              <button
                onClick={() => { onMappingSearchChange(""); onMappingPageChange(1); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        )}

        {mappingSearch.trim() && filteredMappings.length !== sortedMappings.length && (
          <p className="text-sm text-muted-foreground">
            {filteredMappings.length} of {sortedMappings.length} mappings
          </p>
        )}

        {renderMappingBody()}
      </CardContent>
    </>
  );
}
